import React, { useCallback, useEffect, useRef, useState } from 'react';
import './DraggableBugFab.css';

const HANDLE_W = 46;
const HANDLE_H = 68;
const POS_KEY = 'sp_bug_fab_pos';

function clamp(v, min, max) {
    return Math.min(max, Math.max(min, v));
}

function loadPos() {
    try {
        const raw = localStorage.getItem(POS_KEY);
        if (raw) return JSON.parse(raw);
    } catch { /* ignore */ }
    return null;
}

export default function DraggableBugFab({ onPress, title, label = '🐛' }) {
    const [side, setSide] = useState('right');
    const [pos, setPos] = useState({ x: 0, y: 0 });
    const [ready, setReady] = useState(false);
    const dragRef = useRef({ active: false, moved: false, startX: 0, startY: 0, originX: 0, originY: 0 });

    const place = useCallback((targetSide, y, persist = true) => {
        const maxY = window.innerHeight - HANDLE_H - 24;
        const clampedY = clamp(y, 72, maxY);
        const x = targetSide === 'right' ? window.innerWidth - HANDLE_W - 4 : 4;
        setSide(targetSide);
        setPos({ x, y: clampedY });
        if (persist) {
            localStorage.setItem(POS_KEY, JSON.stringify({ side: targetSide, y: clampedY }));
        }
    }, []);

    useEffect(() => {
        const init = () => {
            const saved = loadPos();
            const defaultY = clamp(window.innerHeight * 0.55 - HANDLE_H / 2, 72, window.innerHeight - HANDLE_H - 24);
            if (saved?.side && typeof saved.y === 'number') {
                place(saved.side, saved.y, false);
            } else {
                place('right', defaultY, false);
            }
            setReady(true);
        };
        init();
        const onResize = () => {
            const saved = loadPos();
            if (saved) place(saved.side, saved.y, false);
        };
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [place]);

    const onPointerDown = (e) => {
        dragRef.current = {
            active: true,
            moved: false,
            startX: e.clientX,
            startY: e.clientY,
            originX: pos.x,
            originY: pos.y,
        };
        e.currentTarget.setPointerCapture(e.pointerId);
    };

    const onPointerMove = (e) => {
        if (!dragRef.current.active) return;
        const dx = e.clientX - dragRef.current.startX;
        const dy = e.clientY - dragRef.current.startY;
        if (Math.abs(dx) + Math.abs(dy) > 4) dragRef.current.moved = true;
        setPos({
            x: dragRef.current.originX + dx,
            y: dragRef.current.originY + dy,
        });
    };

    const onPointerUp = (e) => {
        if (!dragRef.current.active) return;
        dragRef.current.active = false;
        try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }

        if (!dragRef.current.moved) {
            onPress?.();
            const saved = loadPos();
            if (saved) place(saved.side, saved.y, false);
            return;
        }

        const releaseX = dragRef.current.originX + (e.clientX - dragRef.current.startX);
        const releaseY = dragRef.current.originY + (e.clientY - dragRef.current.startY);
        const centerX = releaseX + HANDLE_W / 2;
        const newSide = centerX < window.innerWidth / 2 ? 'left' : 'right';
        place(newSide, releaseY);
    };

    if (!ready) return null;

    return (
        <button
            type="button"
            className={`bug-drag-fab bug-drag-fab--${side}`}
            style={{ left: pos.x, top: pos.y, width: HANDLE_W, height: HANDLE_H }}
            title={title}
            aria-label={title}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
        >
            <span className="bug-drag-fab__icon">{label}</span>
        </button>
    );
}
